import { createEmptyPageDocument, createPageComputed } from '../pages';
import {
    type ComputedContentSourceRevision,
    type Revision,
    type RevisionFile,
    RevisionType,
} from '../types';
import { type Edits, randomUUID } from '../utils';

interface CreateEmptyRevisionOptions {
    id?: string;
    contentId: string;
    edits?: Edits;
    files?: Record<string, RevisionFile>;
    rootPages?: Revision['rootPages'];
    pages?: Revision['pages'];
    reusableContents?: Revision['reusableContents'];
}

/**
 * Create an empty revision, as the first revision of a content.
 */
export function createInitialRevision(options: CreateEmptyRevisionOptions): Revision {
    const firstPage = createEmptyPageDocument();

    return createEmptyRevision({
        rootPages: [firstPage.id],
        pages: {
            [firstPage.id]: firstPage,
        },
        ...options,
    });
}

/**
 * Create an empty revision with only one computed content source.
 */
export function createInitialRevisionWithComputedSource(
    options: CreateEmptyRevisionOptions & {
        computedSource: ComputedContentSourceRevision;
    }
): Revision {
    const firstPage = createPageComputed({
        title: 'Initial page',
        computed: options.computedSource,
    });

    return createEmptyRevision({
        rootPages: [firstPage.id],
        pages: {
            [firstPage.id]: firstPage,
        },
        ...options,
    });
}

/**
 * Create an empty revision without any pages.
 */
export function createEmptyRevision(options: CreateEmptyRevisionOptions): Revision {
    return {
        id: options.id || randomUUID(),
        type: RevisionType.Edits,
        contentId: options.contentId,
        parents: [],
        files: options.files ?? {},
        rootPages: options.rootPages ?? [],
        pages: options.pages ?? {},
        edits: options.edits ?? {},
        variables: {},
        reusableContents: options.reusableContents ?? {},
    };
}
